// =============================================================================
// USEDIALOG.JS — Making the modals usable without a mouse.
// =============================================================================
// Every overlay in the app was a plain <div>: no role, no aria-modal, and no
// focus handling. Three things followed from that.
//
//   1. Screen readers never announced that a dialog had opened, and kept
//      reading the page behind it as though it were still the active content.
//   2. Tab walked straight out of the dialog and into the page underneath,
//      where the user could operate controls they could not see.
//   3. Closing a dialog dropped focus back to the top of the document, so a
//      keyboard user lost their place in a 200-row table every time.
//
// One hook, applied at each overlay, fixes all three. It deliberately does not
// render anything — the dialogs each have their own layout and styling, and
// rewriting five of them was a bigger change than this problem warrants.
// =============================================================================

import { useEffect, useRef } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Wire up dialog semantics and focus behaviour.
 *
 * Spread the returned props onto the dialog's own container (the inner panel,
 * not the backdrop):
 *
 *   const dialog = useDialog(onClose, 'Edit deal')
 *   <div {...dialog.props}> ... </div>
 *
 * @param {Function} onClose   Called on Escape. Omit to disable Escape.
 * @param {string}   label     Accessible name announced when the dialog opens.
 * @param {boolean}  autoFocus Focus the first control (false for search boxes
 *                             that already focus themselves).
 * @param {boolean}  active    Pass the dialog's open flag when the hook is
 *                             called from a component that stays mounted while
 *                             the dialog comes and goes (the income breakdown
 *                             inside Dashboard). Without it the effect runs
 *                             once, at a point where the ref is still null, and
 *                             the trap never attaches. Dialogs that mount and
 *                             unmount with their own component can leave it.
 */
export function useDialog(onClose, label, autoFocus = true, active = true) {
  const ref = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!active) return
    const node = ref.current
    if (!node) return

    // Remember where focus came from so it can be handed back on close.
    const previouslyFocused = document.activeElement

    if (autoFocus) {
      const first = node.querySelector(FOCUSABLE)
      if (first) first.focus()
      else node.focus()
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && closeRef.current) {
        e.stopPropagation()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab') return

      // Keep Tab inside the dialog. Re-queried on every press because the
      // dialogs show and hide controls as their forms change.
      const items = [...node.querySelectorAll(FOCUSABLE)]
        .filter(el => el.offsetParent !== null || el === document.activeElement)
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && (document.activeElement === first || !node.contains(document.activeElement))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)

    // The page behind a modal should not be reachable by assistive tech.
    const root = document.getElementById('root')
    const hadAriaHidden = root?.getAttribute('aria-hidden')
    if (root && !root.contains(node)) root.setAttribute('aria-hidden', 'true')

    // Stop the page behind from scrolling under the dialog.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      node.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      if (root && !root.contains(node)) {
        if (hadAriaHidden === null || hadAriaHidden === undefined) root.removeAttribute('aria-hidden')
        else root.setAttribute('aria-hidden', hadAriaHidden)
      }
      // Only restore focus if it is still somewhere in the dialog; if the user
      // has already clicked elsewhere, stealing it back would be worse.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        if (!document.activeElement || document.activeElement === document.body || node.contains(document.activeElement)) {
          previouslyFocused.focus()
        }
      }
    }
  }, [autoFocus, active])

  return {
    ref,
    props: {
      ref,
      role: 'dialog',
      'aria-modal': true,
      'aria-label': label,
      tabIndex: -1,
    },
  }
}
